/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * inlineSubworkflow.ts  –  Utility to *flatten* one level of nesting in a
 * CWL `Workflow`.  It replaces a step that **runs** a sub‑workflow with all
 * of that sub‑workflow’s internal steps, wiring connections so nothing
 * breaks.
 *   • Only **one** step is inlined per call (identified by `stepId`).
 *   • Sub‑workflow contains only CommandLineTool/ExpressionTool steps –
 *     no further nested Workflows. (Call the function again for deeper
 *     levels.)
 *   • Sources are expressed as strings – if you rely on `linkMerge` or
 *     `valueFrom`, extend the helper where commented.
 *
 * Adapt or extend as needed – the code is self‑contained & type‑checked.
 */

import { Workflow as WorkflowModel, WorkflowStepInput, WorkflowStep, Process } from 'cwlts/mappings/v1.0';
import { flattenWorkflow } from './cwl-flatten.ts';

/** Deep‑clone primitive (JSON‑only) – good enough for CWL maps */
const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x));

/**
 * Flatten `stepId` inside `parent` by inlining all steps from `sub`.
 *
 * @param parent  Workflow that *contains* the `stepId` step.
 * @param stepId  Step in `parent.steps` whose `run` points at `sub.id`.
 * @param sub     The sub‑workflow document to inline.
 * @returns       **New** Workflow instance (does not mutate inputs).
 */
export const inlineSubworkflow = (parent: WorkflowModel, stepId: string, sub: WorkflowModel): WorkflowModel => {
  /* ── 1. Sanity checks ─────────────────────────────────────────── */
  if (!parent.steps?.[stepId]) {
    throw new Error(`Step '${stepId}' not found in parent workflow '${parent.id}'.`);
  }
  const callerStep = parent.steps[stepId];
  if ((callerStep.run as string)?.replace(/^#/, '') !== sub.id) {
    throw new Error(`Step '${stepId}' does not reference sub‑workflow '#${sub.id}'.`);
  }

  /* ── 2. Prepare mapping helpers ──────────────────────────────── */
  const prefix = ``; // ensures unique IDs inside parent

  const idMap = Object.keys(sub.steps ?? {}).reduce<Record<string, string>>((acc, internalId) => {
    acc[internalId] = `${prefix}${internalId}`;
    return acc;
  }, {});

  const mapStepRef = (src: string): string => {
    const [sid, port] = src.split('/');
    return idMap[sid] ? `${idMap[sid]}/${port}` : src;
  };

  /* Map sub‑workflow *inputs* to whatever the caller provided */
  const inputBindings = callerStep.in ?? {}; // subInName → parentSrc

  /* ── 3. Clone & rewrite every internal step ──────────────────── */
  const newSteps: Record<string, WorkflowStep> = {};

  Object.entries(sub.steps ?? {}).forEach(([oldId, oldStep]) => {
    const newId = idMap[oldId];
    const s: WorkflowStep = clone(oldStep);
    s.id = newId; // ensure unique ID in parent

    /* adjust all *in* bindings */
    if (s.in) {
      Object.entries<WorkflowStepInput>(s.in).forEach(([inpName, inpVal]) => {
        // Format 1: shorthand string
        if (typeof inpVal === 'string') {
          // ① bound to parent via input → use callerStep mapping
          if (inputBindings[inpVal]) {
            (s.in as unknown)[inpName] = inputBindings[inpVal];
          } else {
            (s.in as unknown)[inpName] = mapStepRef(inpVal);
          }
          return;
        }

        // Format 2: object with {source: string | string[]} etc.
        if (typeof inpVal === 'object' && inpVal !== null && 'source' in inpVal) {
          const srcField = inpVal.source as unknown;
          if (typeof srcField === 'string') {
            inpVal.source = inputBindings[srcField] ?? mapStepRef(srcField);
          } else if (Array.isArray(srcField)) {
            inpVal.source = srcField.map((s) => inputBindings[s] ?? mapStepRef(s));
          }
        }
      });
    }

    newSteps[newId] = s;
  });

  /* ── 4. Build mapping for sub‑workflow *outputs* ─────────────── */
  const subOutMap: Record<string, string | string[]> = {};
  Object.entries(sub.outputs ?? {}).forEach(([outId, outDef]) => {
    const src = outDef.outputSource as unknown;
    const rewrite = (s: string) => mapStepRef(s);
    if (Array.isArray(src)) {
      subOutMap[`${stepId}/${outId}`] = src.map(rewrite);
    } else {
      if (typeof src === 'string') {
        subOutMap[`${stepId}/${outId}`] = rewrite(src);
      }
    }
  });

  /* ── 5. Clone parent, remove caller step, insert new steps ───── */
  const nextParent: WorkflowModel = clone(parent);
  delete nextParent.steps![stepId];
  nextParent.steps = {
    ...nextParent.steps,
    ...newSteps
  };

  /* ── 6. Rewrite *existing* references to caller’s outputs ────── */
  const patchInBindings = (bindings) => {
    Object.entries(bindings).forEach(([k, v]) => {
      if (typeof v === 'string' && subOutMap[v]) {
        bindings[k] = subOutMap[v];
      }
      if (typeof v === 'object' && 'source' in v && v.source) {
        if (typeof v.source === 'string' && subOutMap[v.source]) {
          v.source = subOutMap[v.source];
        }
        if (Array.isArray(v.source)) {
          v.source = v.source.flatMap((s: string) => subOutMap[s] ?? s);
        }
      }
    });
  };

  // 6a. Other steps’ inputs
  Object.entries(nextParent.steps).forEach(([id, st]) => {
    if (id === stepId) {
      return;
    }
    if (st.in) {
      patchInBindings(st.in);
    }
  });

  // 6b. Workflow outputs
  Object.values(nextParent.outputs ?? {}).forEach((out) => {
    if (!out.outputSource) {
      return;
    }
    if (typeof out.outputSource === 'string' && subOutMap[out.outputSource]) {
      out.outputSource = subOutMap[out.outputSource];
    } else if (Array.isArray(out.outputSource)) {
      out.outputSource = out.outputSource.flatMap((s) => subOutMap[s] ?? s);
    }
  });

  return nextParent;
};

const findTopLevelWorkflowForPack = (cwlRaw: Process): WorkflowModel | null => {
  const workflowItem = cwlRaw['$graph'].find((el) => {
    if (el.class.toLowerCase() === 'workflow') {
      const worflow = el as WorkflowModel;
      if (worflow.steps) {
        if (worflow.steps instanceof Array) {
          return worflow.steps.some((step) => step.id === 'node_wf');
        }
        return Object.entries(worflow.steps).some(([step]) => step === 'node_wf');
      }
    }

    return false;
  });
  return (workflowItem as WorkflowModel) ?? null;
};

type ExtractOptions = { raw: string; mainWorkflowId: string; pack?: boolean };
export const extractCWLGraphFromRaw = ({ raw, mainWorkflowId, pack = false }: ExtractOptions): WorkflowModel | null => {
  try {
    const cwlRaw = JSON.parse(raw);
    const workflowItem = cwlRaw['$graph'].find(
      (el) => el.class.toLowerCase() === 'workflow' && el.id.startsWith(mainWorkflowId)
    );
    const cwl = (workflowItem as WorkflowModel) ?? null;
    if (pack) {
      const topLevelWorkflow = findTopLevelWorkflowForPack(cwlRaw);
      if (topLevelWorkflow) {
        // flattering the top-level workflow will inline all sub-workflows for visualization
        const rs = flattenWorkflow(topLevelWorkflow, cwlRaw['$graph']);
        return rs;
      }
    }
    if (cwl) {
      if (cwl.cwlVersion === undefined) {
        cwl.cwlVersion = 'v1.0';
      }
    }
    return cwl;
  } catch (e) {
    throw new Error(e);
  }
};
